import { useContext } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import DesignToolActions from '../contexts/DesignTool/DesignToolActions';
import { DesignToolContext } from '../contexts/DesignTool/DesignToolContext';
import LeftSideBarActions from '../contexts/DesignTool/LeftSideBarActions';
import { amazonUploadingServices } from '../services/request/s3ImageUpload';
import { defaultBackgroundProps, stageDimensions } from '../utils/defaults';
import Resizer from '../utils/imageResizer';
import useCardNavigation from './useCardNavigation';
import type { TRightSideBar } from '../contexts/DesignTool/types';
const useCardToolFunctions = () => {
  const {
      designToolState: { selectedShapeName, activePageIndex, right_side_bar_selection },
      setCardPagesX,
      cardPages,
      setColorPickerShowModal,
      setRefetch,
      copiedData,
  } = useContext(DesignToolContext);

	const { unSelect } = useCardNavigation();
	const { setSelectedShapeName, resetStateMachine } = DesignToolActions();
	const { selectElement } = LeftSideBarActions();
	const userProfile = useSelector((state: any) => state.userProfile);
	const currentWorkspace = useSelector((state: any) => state.currentWorkspace);
	/**
	 *
	 * This function is used to add text on stage with defaults styling.
	 */

	const isBackgroundImage = () =>
		right_side_bar_selection.element === 'image' && right_side_bar_selection.type === 'background';

	const handleText = () => {
		// `addedby` will be undefined in case of collaborator because we do not save firebase Id
		let addedby = userProfile._id;

		setCardPagesX((prev: any) => {
			const newId = 'textBoxes_' + new Date().getTime().toString();
			const stageDimension: any = { ...stageDimensions };
			const updatedShape = {
				id: newId,
				fontFamily: 'Montserrat',
				fontStyle: 'normal',
				lineHeight: 1,
				letterSpacing: 0,
				verticalAlign: 'top',
				fill: '#000000',
				fontSize: 25,
				align: 'left',
				addedby,
				text: 'Click to add text',
				name: 'object',
				x: (stageDimension.width - 180) / 2,
				y: (stageDimension.height - 120) / 2,
				isNew: true,
				draggable: true,
				width: 200,
				height: 50,
				effectType: 'none',
			};
			prev[activePageIndex]?.textBoxes.push(updatedShape);
			setSelectedShapeName({
				shape_id: newId,
				shapeType: { element: 'text', type: 'fontTool' },
			});
			selectElement({ element: 'text', type: 'fontTool' });
		});
	};
  const handleTextPaste = () => {
    if (!copiedData?.id) {
      return;
    }
    const shapeType = String(copiedData.id || '').split('_');
    if (shapeType[0] === 'textBoxes') {
			setCardPagesX((prev: any) => {
				const newId = 'textBoxes_' + new Date().getTime().toString();
				const updatedShape = {
					id: newId,
					fontFamily: copiedData.fontFamily,
					fontStyle: copiedData.fontStyle,
					fill: copiedData.fill,
					fontSize: copiedData.fontSize,
					align: copiedData.align,
					text: copiedData.text,
					name: copiedData.name,
					x: copiedData.x + 20,
					y: copiedData.y + 50,
					isNew: copiedData.isNew,
					draggable: copiedData.draggable,
					width: copiedData.width,
					height: copiedData.height,
				};
				prev[activePageIndex]?.textBoxes.push(updatedShape);
				setSelectedShapeName({
					shape_id: newId,
					shapeType: { element: 'text', type: 'fontTool' },
				});
				selectElement({ element: 'text', type: 'fontTool' });
			});
		} else if (shapeType[0] === 'images') {
			const pageIndexForDownloadUrl = activePageIndex;
			let imageId: any = new Date().getTime();
			imageId = 'images_' + imageId.toString();
			setCardPagesX((prev: any) => {
				prev[pageIndexForDownloadUrl].images.push({
					draggable: copiedData.draggable,
					name: copiedData.name,
					id: imageId,
					downloadUrl: copiedData.downloadUrl,
					x: copiedData.x + 20,
					y: copiedData.y + 50,
					scaleX: copiedData.scaleX,
					scaleY: copiedData.scaleY,
					width: copiedData.width,
					height: copiedData.height,
				});
				setSelectedShapeName({
					shape_id: imageId,
					shapeType: { element: 'image', type: null },
				});
			});
		} else if (shapeType[0] === 'gifs') {
			const pageIndexForDownloadUrl = activePageIndex;
			let gifId: any = new Date().getTime();
			gifId = 'gifs_' + gifId.toString();
			setCardPagesX((prev: any) => {
				const updatedGif = {
					draggable: copiedData.draggable,
					name: copiedData.name,
					id: gifId,
					src: copiedData.src,
					x: copiedData.x + 40,
					y: copiedData.y + 40,
					scaleX: copiedData.scaleX,
					scaleY: copiedData.scaleY,
					width: copiedData.width,
					height: copiedData.height,
				};
				if (prev[pageIndexForDownloadUrl]?.gifs) {
					prev[pageIndexForDownloadUrl].gifs.push(updatedGif);
				} else {
					prev[activePageIndex] = {
						...prev[activePageIndex],
						gifs: [updatedGif],
					};
				}
				setSelectedShapeName({
					shape_id: gifId,
					shapeType: { element: 'gif', type: null },
				});
			});
		} else if (shapeType[0] === 'ctas') {
			setCardPagesX((prev: any) => {
				const newId = 'ctas_' + new Date().getTime().toString();
				const updatedShape = {
					id: newId,
					name: copiedData.name,
					x: copiedData.x + 20,
					y: copiedData.y + 50,
					draggable: copiedData.draggable,
					width: copiedData.width,
					height: copiedData.height,
					urlLink: copiedData.urlLink,
					text: copiedData.text,
					btnColor: copiedData.btnColor,
					textColor: copiedData.textColor,
					borderRadius: copiedData.borderRadius,
				};
				if (prev[activePageIndex]?.ctas) {
					prev[activePageIndex]?.ctas.push(updatedShape);
				} else {
					prev[activePageIndex] = {
						...prev[activePageIndex],
						ctas: [updatedShape],
					};
				}
				setSelectedShapeName({
					shape_id: newId,
					shapeType: { element: 'cta', type: null },
				});
                selectElement({ element: 'cta', type: null });
            });
    } else if (shapeType[0] === 'videos') {
        const pageIndex = activePageIndex;
        let id: any = new Date().getTime();
        id = 'videos_' + id.toString();
        setCardPagesX((prev: any) => {
            if (!prev[pageIndex]) prev[pageIndex] = { textBoxes: [], images: [], gifs: [], ctas: [], rects: [], videos: [] };
            if (!Array.isArray(prev[pageIndex].videos)) prev[pageIndex].videos = [];
            prev[pageIndex].videos.push({
                id,
                name: copiedData.name,
                draggable: copiedData.draggable,
                src: copiedData.src,
                width: copiedData.width,
                height: copiedData.height,
                x: (copiedData.x || 0) + 20,
                y: (copiedData.y || 0) + 50,
            });
            return prev;
        });
        setSelectedShapeName({ shape_id: id, shapeType: { element: 'video', type: null } });
    }
  };

  const handleAddVideo = (url: string) => {
    const pageIndex = activePageIndex;
    const addedby = userProfile?._id;
    let id: any = new Date().getTime();
    id = 'videos_' + id.toString();

    // initial dims similar to images/gifs (75% of stage)
    const displayWidth = stageDimensions.width * 0.75;
    const displayHeight = stageDimensions.height * 0.75;
    const x = (stageDimensions.width - displayWidth) / 2;
    const y = (stageDimensions.height - displayHeight) / 2;

    setCardPagesX((prev: any) => {
      if (!prev[pageIndex]) prev[pageIndex] = { textBoxes: [], images: [], gifs: [], ctas: [], rects: [], videos: [] };
      if (!Array.isArray(prev[pageIndex].videos)) prev[pageIndex].videos = [];

      prev[pageIndex].videos.push({
        id,
        name: 'object',
        draggable: true,
        addedby,
        src: url,
        width: displayWidth,
        height: displayHeight,
        x,
        y,
      });
      return prev;
    });

    setSelectedShapeName({ shape_id: id, shapeType: { element: 'video', type: null } });
  };

  const handleVideoUpload = async (e: any, updateLoadingState?: (loading: boolean) => void) => {
    const vid: File | undefined = e?.target?.files?.[0];
    if (e?.target) e.target.value = '';
    if (!vid) return;
    if (!vid.type?.startsWith('video/')) {
      toast.error('Please select a valid video file', { position: 'top-right', autoClose: 3000 });
      return;
    }
    const MAX = 100 * 1024 * 1024; // 100MB
    if (vid.size > MAX) {
      toast.error('Video too large (max 100MB)', { position: 'top-right', autoClose: 4000 });
      return;
    }
    try {
      if (updateLoadingState) updateLoadingState(true);
      toast.info('Uploading video...', { position: 'top-right', autoClose: 2000 });
      const url = await amazonUploadingServices.uploadUserVideo(vid, currentWorkspace?._id, userProfile?._id);
      handleAddVideo(url);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to upload video');
    } finally {
      if (updateLoadingState) updateLoadingState(false);
    }
  };

	const handleAddCta = () => {
		// `addedby` will be undefined in case of collaborator because we do not save firebase Id
		let addedby = userProfile._id;

		setCardPagesX((prev: any) => {
			const newId = 'ctas_' + new Date().getTime().toString();
			const stageDimension: any = { ...stageDimensions };
			const updatedShape = {
				id: newId,
				addedby,
				name: 'object',
				x: (stageDimension.width - 180) / 2,
				y: (stageDimension.height - 120) / 2,
				draggable: true,
				width: 150,
				height: 50,
				urlLink: 'https://example.com',
				text: 'Click Me!',
				btnColor: '#171717',
				textColor: '#FFFFFF',
				borderRadius: 'rounded',
			};
			if (prev[activePageIndex]?.ctas) {
				prev[activePageIndex]?.ctas.push(updatedShape);
			} else {
				prev[activePageIndex] = {
					...prev[activePageIndex],
					ctas: [updatedShape],
				};
			}
			setSelectedShapeName({
				shape_id: newId,
				shapeType: { element: 'cta', type: null },
			});
			selectElement({ element: 'cta', type: null });
		});
	};
	/**
	 * This function is getting the selected element ( Text and images ) data.
	 *
	 */
	const getSelectedElementData = () => {
		if (selectedShapeName) {
			const shapeType = selectedShapeName?.split('_')[0];
			const selectedShape = cardPages[activePageIndex][shapeType]?.find((item: any) => item?.id === selectedShapeName);
			return selectedShape;
		} else {
			return null;
		}
	};
	const selectedShapeData = getSelectedElementData();
	/**
	 * This function is used to change text styling of selected text on stage. example alignment, font Style, font family.
	 */
	const handleTextProps = (type: string, value: any) => {
		setCardPagesX((prev: any) => {
			prev[activePageIndex]?.textBoxes?.find((item: any, index: any) => {
				if (item.id === selectedShapeName) {
					prev[activePageIndex].textBoxes[index][type] = value;
					if (type !== 'fill') {
						setColorPickerShowModal(false);
					}
					return true;
				}
				return false;
			});
		});
	};
	/**
	 * This function is used to change cta styling of selected cta on stage. example url, color, text color, etc.
	 */
	const handleCtaProps = (type: string, value: any) => {
		setCardPagesX((prev: any) => {
			prev[activePageIndex].ctas.find((item: any, index: any) => {
				if (item.id === selectedShapeName) {
					prev[activePageIndex].ctas[index][type] = value;
					return true;
				}
				return false;
			});
		});
	};
	/**
	 * This function is used to change selected text effects (glitch, neon, lift, splice ).
	 * reason for creating this function separately is that we need to set multiple attributes of text here.
	 */
	const handleTextEffect = (textObj: any) => {
		setCardPagesX((prev: any) => {
			prev[activePageIndex].textBoxes.find((item: any, index: any) => {
				if (item.id === selectedShapeName) {
					prev[activePageIndex].textBoxes[index] = textObj;
					return true;
				}
				return false;
			});
		});
	};
	const handleAddBackgroundColor = (color: string) => {
		const pageIndexForBgColor = activePageIndex;
		let rectId: any = new Date().getTime();
		rectId = 'rects_' + rectId.toString();
		let addedby = userProfile._id;
		setCardPagesX((prev: any) => {
			const updatedBgColor = {
				draggable: false,
				name: 'object',
				id: rectId,
				addedby,
				x: 0,
				y: 0,
				width: stageDimensions.width,
				height: stageDimensions.height,
				fill: color,
			};
			prev[pageIndexForBgColor] = {
				...prev[pageIndexForBgColor],
				rects: [updatedBgColor],
			};
		});
	};
	/**
	 *
	 * This function is used to add image and gif on stage with defaults styling.
	 */
const placeExistingImageOnStage = (
	downloadUrl: string,
	originalWidth?: number,
	originalHeight?: number,
	desiredSidePanelType?: TRightSideBar['type'],
) => {
	const pageIndexForDownloadUrl = activePageIndex;
	const stageWidth = stageDimensions.width;
	const stageHeight = stageDimensions.height;
	const addedby = userProfile?._id;
	let insertedId: string | null = null;

	setCardPagesX((prev: any) => {
		if (!prev[pageIndexForDownloadUrl]) {
			prev[pageIndexForDownloadUrl] = { textBoxes: [], images: [] };
		}
		if (!Array.isArray(prev[pageIndexForDownloadUrl].images)) {
			prev[pageIndexForDownloadUrl].images = [];
		}

		const images = prev[pageIndexForDownloadUrl].images;

		if (isBackgroundImage()) {
			const smartCroppedImage = downloadUrl;
			insertedId = defaultBackgroundProps.id;

			if (!images[0]) {
				images[0] = { ...defaultBackgroundProps, downloadUrl: smartCroppedImage };
			} else if (images[0]?.name === 'object') {
				images.splice(0, 0, { ...defaultBackgroundProps, downloadUrl: smartCroppedImage });
			} else {
				images[0] = { ...defaultBackgroundProps, downloadUrl: smartCroppedImage };
			}
			return prev;
		}

		const displayWidth = stageWidth * 0.75;
		const displayHeight = stageHeight * 0.75;
		let imageWidth = displayWidth;
		let imageHeight = displayHeight;
		let ratio = originalWidth ? originalWidth / displayWidth : 1;

		if (originalWidth && originalWidth < displayWidth) {
			imageWidth = originalWidth;
			ratio = 1;
		}

		if (originalHeight && originalWidth) {
			imageHeight = originalHeight / (ratio || 1);
		} else if (originalHeight && !originalWidth) {
			imageHeight = originalHeight;
		}

		const imageX = (stageWidth - imageWidth) / 2;
		const imageY = (stageHeight - imageHeight) / 2;
		const imageId = `images_${Date.now()}`;

		images.push({
			draggable: true,
			name: 'object',
			id: imageId,
			addedby,
			downloadUrl,
			x: imageX,
			y: imageY,
			width: imageWidth,
			height: imageHeight,
		});

		insertedId = imageId;
		return prev;
	}, false);

	if (insertedId && insertedId !== defaultBackgroundProps.id) {
		setSelectedShapeName({
			shape_id: insertedId,
			shapeType: { element: 'image', type: desiredSidePanelType ?? null },
		});
	}
};

const handleImage = async (value: any, width?: any, height?: any): Promise<void> => {
		if (value.file.type === 'image/gif') {
			try {
				toast.info('Uploading gif!', {
					position: 'top-right',
					autoClose: 5000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					pauseOnFocusLoss: false,
				});
				const downloadUrl = await amazonUploadingServices.uploadUserImage(
					value.file,
					'GIF',
					currentWorkspace?._id,
					userProfile?._id
				);

				handleAddGif(downloadUrl, width, height);
			} catch (error: any) {
				toast.error(error.message, {
					position: 'top-right',
					autoClose: 5000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					pauseOnFocusLoss: false,
				});
			}
	} else {
		try {
				toast.info('Uploading image!', {
					position: 'top-right',
					autoClose: 5000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					pauseOnFocusLoss: false,
				});
				let downloadUrl = await amazonUploadingServices.uploadUserImage(
					value.file,
					'IMAGE',
					currentWorkspace?._id,
					userProfile?._id
				);

				if (downloadUrl) {
					console.log('THE DOWNLOAD URL =>', downloadUrl);
					placeExistingImageOnStage(downloadUrl, width, height);
				} else {
					throw new Error('There was a problem while uploading image.');
				}
			} catch (error: any) {
				toast.error(error.message, {
					position: 'top-right',
					autoClose: 5000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					pauseOnFocusLoss: false,
				});
			}
		}
		setRefetch(true);
	};

const handleExistingMediaImage = (
	image: { src: string; width?: number; height?: number },
	desiredSidePanelType?: TRightSideBar['type'],
) => {
	if (!image?.src) return;
	placeExistingImageOnStage(image.src, image.width, image.height, desiredSidePanelType);
};

	const handleAddGif = (src: string, width: number, height: number) => {
		const pageIndexForDownloadUrl = activePageIndex;
		// let downloadUrl = "https://media.giphy.com/media/vVzH2XY3Y0Ar6/giphy.gif";
		let gifId: any = new Date().getTime();
		gifId = 'gifs_' + gifId.toString();
		// `addedby` will be undefined in case of collaborator because we do not save firebase Id
		let addedby = userProfile._id;

		setCardPagesX((prev: any) => {
			const displayDimensions = {
				width: stageDimensions.width * 0.75,
				height: stageDimensions.height * 0.75,
			};
			let gifWidth = displayDimensions.width,
				gifHeight = displayDimensions.height;
			let gifX = 0,
				gifY = 0;
			let ratio = width / displayDimensions.width;
			if (width < displayDimensions.width) {
				gifWidth = width;
				ratio = 1;
			}
			gifHeight = height / ratio;
			gifX = (stageDimensions.width - gifWidth) / 2;
			gifY = (stageDimensions.height - gifHeight) / 2;

			const updatedGif = {
				draggable: true,
				name: 'object',
				id: gifId,
				addedby,
				src,
				x: gifX,
				y: gifY,
				width: gifWidth,
				height: gifHeight,
			};

			if (prev[pageIndexForDownloadUrl]?.gifs) {
				prev[pageIndexForDownloadUrl].gifs.push(updatedGif);
			} else {
				prev[activePageIndex] = {
					...prev[activePageIndex],
					gifs: [updatedGif],
				};
			}

			setSelectedShapeName({
				shape_id: gifId,
				shapeType: { element: 'gif', type: null },
			});
		});
	};
	const handleUserImageUploads = (downloadUrl: string, imgWidth: number, imgHeight: number) => {
		const pageIndexForDownloadUrl = activePageIndex;
		let imageId: any = new Date().getTime();
		imageId = 'images_' + imageId.toString();
		// `addedby` will be undefined in case of collaborator because we do not save firebase Id
		let addedby = userProfile._id;

		setCardPagesX((prev: any) => {
			if (isBackgroundImage()) {
				const smartCroppedImage = downloadUrl;
				if (!cardPages?.[activePageIndex]?.images[0]) {
					prev[pageIndexForDownloadUrl].images[0] = {
						...defaultBackgroundProps,
						downloadUrl: smartCroppedImage,
					};
				}
				if (!!cardPages[activePageIndex].images[0] && cardPages[activePageIndex].images[0]?.name === 'object') {
					prev[pageIndexForDownloadUrl].images.splice(0, 0, {
						...defaultBackgroundProps,
						downloadUrl: smartCroppedImage,
					});
				} else {
					prev[pageIndexForDownloadUrl].images[0] = {
						...defaultBackgroundProps,
						downloadUrl: smartCroppedImage,
					};
				}
			} else {
				const displayDimensions = {
					width: stageDimensions.width * 0.75,
					height: stageDimensions.height * 0.75,
				};
				let imageWidth = displayDimensions.width,
					imageHeight = displayDimensions.height;
				let imageX = 0,
					imageY = 0;
				let ratio = imgWidth / displayDimensions.width;
				if (imgWidth < displayDimensions.width) {
					imageWidth = imgWidth;
					ratio = 1;
				}
				imageHeight = imgHeight / ratio;
				imageX = (stageDimensions.width - imageWidth) / 2;
				imageY = (stageDimensions.height - imageHeight) / 2;

				prev[pageIndexForDownloadUrl].images.push({
					draggable: true,
					name: 'object',
					id: imageId,
					addedby,
					downloadUrl,
					x: imageX,
					y: imageY,
					width: imageWidth,
					height: imageHeight,
				});

				setSelectedShapeName({
					shape_id: imageId,
					shapeType: { element: 'image', type: null },
				});
			}
		});
	};

	/**
	 *
	 * This function is used to add image from user device on stage with defaults styling.
	 */
	const handleImageUpload = (e: any, updateLoadingState?: (loading: boolean) => void) => {
		const img = e.target.files[0];

		// Reset the input value to allow selecting the same file again
		e.target.value = '';

		if (!img) {
			toast.error('No file selected', {
				position: 'top-right',
				autoClose: 3000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
				pauseOnFocusLoss: false,
			});
			return;
		}

		const is10MB = 1024 * 1024 * 10;
		if (img.size > is10MB) {
			toast.error('Image too large! Please select an image smaller than 10MB', {
				position: 'top-right',
				autoClose: 5000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
				pauseOnFocusLoss: false,
			});
			return;
		}

		if (img.type === 'image/gif') {
			if (updateLoadingState) updateLoadingState(true);
			const reader = new FileReader();
			reader.readAsDataURL(img);
			reader.onload = (event: any) => {
				const image = new Image();
				image.src = event.target.result;
				image.onload = () => {
					handleImage(
						{ file: img, name: event.target.result, width: image.width, height: image.height },
						image.width,
						image.height
					).finally(() => {
						if (updateLoadingState) updateLoadingState(false);
					});
				};
			};
			return;
		}

		const size = img?.size;
		resizeAndUpload({ size, file: img }, updateLoadingState);
	};
	/**
	 *
	 * This function is used to resize user device upload images or image collection images before adding it on stage.
	 */
	const resizeAndUpload = (
		{ size, file }: { size: number; file: any },
		updateLoadingState?: (loading: boolean) => void
	) => {
		const is10MB=1024*1024*10;
		if (!file) {
			toast.error('Invalid file selected', {
				position: 'top-right',
				autoClose: 3000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
				pauseOnFocusLoss: false,
			});
			return;
		}

		if (size > is10MB) {
			toast.error('Image too large! Please select an image smaller than 2MB', {
				position: 'top-right',
				autoClose: 5000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
				pauseOnFocusLoss: false,
			});
			return;
		}

		// Set loading state if provided
		if (updateLoadingState) {
			updateLoadingState(true);
		}

		const stageDims = { ...stageDimensions };
		Resizer.imageFileResizer(
			file,
			stageDims.width,
			stageDims.height,
			'png',
			100,
			0,
			(uri: any, width: number, height: number) => {
				handleImage({ file, name: uri, width, height }, width, height).finally(() => {
					// Clear loading state when upload completes
					if (updateLoadingState) {
						updateLoadingState(false);
					}
				});
			},
			'base64'
		);
	};
	/**
	 *
	 * This function is used to add image from Image collection like pexels, unsplash, pixabay on stage with defaults styling.
	 */
	const handleExternalImage = async (url: any) => {
		let file = await fetch(url)
			.then((r) => r.blob())
			.then((blobFile) => new File([blobFile], 'canva_image', { type: 'image/png' }))
			.catch((err) => console.log(err));
		resizeAndUpload({ size: 100, file: file });
	};
	/**
	 *
	 * This function is used to delete selected element from the stage.
	 */
  const handleDeleteSelectedItem = ({ selectedShapeName }: { selectedShapeName: string | null }) => {
      if (selectedShapeName) {
          const shapeType = selectedShapeName?.split('_')[0];
          setCardPagesX((prev: any) => {
              prev[activePageIndex][shapeType] = cardPages[activePageIndex][shapeType]?.filter(
                  (item: any) => item.id !== selectedShapeName
              );
          });
          unSelect();
      }
  };
	const handleDeleteBackgroundImage = () => {
		setCardPagesX((prev: any) => {
			prev[activePageIndex].images = cardPages[activePageIndex].images?.filter(
				(item: any) => item.id !== 'images_background'
			);
		});
		unSelect();
	};

	/**
	 *
	 * This function is used to bring back the selected image relative to other images from the stage.
	 */
	const onBringtoBack = () => {
		setCardPagesX((prev: any) => {
			const imageIndex = prev[activePageIndex].images.findIndex((item: any) => item.id === selectedShapeName);
			if (imageIndex > 0) {
				const temp = prev[activePageIndex].images[imageIndex - 1];
				prev[activePageIndex].images[imageIndex - 1] = prev[activePageIndex].images[imageIndex];
				prev[activePageIndex].images[imageIndex] = temp;
			} else {
			}
		});
	};
	/**
	 *
	 * This function is used to bring back to front the selected image relative to other images from the stage.
	 */
	const onBringtoFront = () => {
		setCardPagesX((prev: any) => {
			const imageIndex = prev[activePageIndex].images.findIndex((item: any) => item.id === selectedShapeName);
			if (imageIndex < prev[activePageIndex].images.length - 1) {
				const temp = prev[activePageIndex].images[imageIndex + 1];
				prev[activePageIndex].images[imageIndex + 1] = prev[activePageIndex].images[imageIndex];
				prev[activePageIndex].images[imageIndex] = temp;
			} else {
			}
		});
	};
	/**
	 *
	 * This function is used to change and save element modifications.
	 */

	const handleElementChanges = (e: any) => {
		let rectAttrs = JSON.parse(JSON.stringify(e.target.attrs));
		const { image, ...rect } = rectAttrs;
		const shapeType: any = rect?.id?.split('_')[0];
		setCardPagesX((prev: any) => {
			prev[activePageIndex][shapeType]?.find((item: any, index: any) => {
				if (item?.id === rect?.id) {
					prev[activePageIndex][shapeType][index] = {
						...prev[activePageIndex][shapeType][index],
						...rect,
					};
					return true;
				}
				return false;
			});
		});
	};
	const handleFitToPage = () => {
		const stageDimension: any = { ...stageDimensions };
		setCardPagesX((prev: any) => {
			prev[activePageIndex].images.find((item: any, index: any) => {
				if (item.id === selectedShapeName) {
					const isFitToPage = !!selectedShapeData.fitToPage?.status;
					console.log({ isFitToPageisFitToPageisFitToPage: isFitToPage });
					let fitToPage = {
						status: !isFitToPage,
						originalAttrs: {
							x: !!isFitToPage ? selectedShapeData.fitToPage.originalAttrs.x : selectedShapeData.x,
							y: !!isFitToPage ? selectedShapeData.fitToPage.originalAttrs.y : selectedShapeData.y,
							width: !!isFitToPage ? selectedShapeData.fitToPage.originalAttrs.width : selectedShapeData.width,
							height: !!isFitToPage ? selectedShapeData.fitToPage.originalAttrs.height : selectedShapeData.height,
						},
					};

					const updatedShape = {
						...selectedShapeData,
						fitToPage,
						scaleX: 1,
						scaleY: 1,
						x: !!isFitToPage ? selectedShapeData.fitToPage?.originalAttrs.x : 0,
						y: !!isFitToPage ? selectedShapeData.fitToPage?.originalAttrs.y : 0,
						width: !!isFitToPage ? selectedShapeData.fitToPage?.originalAttrs.width : stageDimension.width,
						height: !!isFitToPage ? selectedShapeData.fitToPage?.originalAttrs.height : stageDimension.height,
						rotation: 0,
					};
					prev[activePageIndex].images[index] = updatedShape;
					return true;
				}
				return false;
			});
		}, false);
	};
	const resetDesignToolState = () => {
		resetStateMachine();
	};

  const resetCardPages = () => {
    setCardPagesX([{ textBoxes: [], images: [], ctas: [], gifs: [], rects: [], videos: [] }]);
  };

  return {
      isBackgroundImage,
      handleImage,
      handleAddGif,
      handleAddVideo,
      handleVideoUpload,
      handleText,
      handleTextPaste,
      handleAddCta,
      handleTextProps,
      handleCtaProps,
      handleImageUpload,
      handleExternalImage,
      handleExistingMediaImage,
      handleUserImageUploads,
      getSelectedElementData,
      handleDeleteSelectedItem,
      onBringtoBack,
      onBringtoFront,
      handleElementChanges,
      handleFitToPage,
      handleTextEffect,
      resetDesignToolState,
      resetCardPages,
      handleDeleteBackgroundImage,
      handleAddBackgroundColor,
  };
};

export default useCardToolFunctions;
